/**
 * Eventos do Traccar, traduzidos e classificados por severidade.
 *
 * A fonte dos tipos é `ops/traccar/l10n/pt_BR.json` — o arquivo de tradução do
 * traccar-web, guardado no repositório. Lá as chaves levam prefixo:
 * `deviceStopped` é `eventDeviceStopped`, `powerCut` é `alarmPowerCut`.
 * `ops/conferir-traducoes.py` compara os dois e avisa o que falta.
 *
 * ⚠️ Os rótulos aqui **não** são cópia do JSON, e é de propósito. O Traccar
 * fala de "dispositivo"; quem usa isto tem uma frota, então é "Parou", não
 * "Dispositivo parado". Onde o texto de lá é pior para o operador, o nosso
 * ganha — e onde ele está errado, também: `footBrake` é freio de PÉ, o pt_BR
 * oficial traduz como "freio de mão".
 *
 * ⚠️ A severidade é nossa, e não vem de lugar nenhum: o Traccar não distingue
 * "alarme de pânico" de "dispositivo parou", e num feed ao vivo essa diferença
 * é tudo.
 */

export type Severidade = "critico" | "atencao" | "info";

export const EVENTO: Record<string, { rotulo: string; sev: Severidade }> = {
  // críticos — exigem reação humana
  alarm:              { rotulo: "Alarme",               sev: "critico" },
  deviceFuelDrop:     { rotulo: "Queda de combustível", sev: "critico" },
  unaccompaniedMotion:{ rotulo: "Movimento sem motorista", sev: "critico" },
  // atenção — fora do esperado
  deviceOverspeed:    { rotulo: "Excesso de velocidade", sev: "atencao" },
  geofenceExit:       { rotulo: "Saiu da cerca",        sev: "atencao" },
  geofenceEnter:      { rotulo: "Entrou na cerca",      sev: "atencao" },
  geofenceCrossed:    { rotulo: "Atravessou a cerca",   sev: "atencao" },
  proximityEnter:     { rotulo: "Aproximou-se",         sev: "atencao" },
  proximityExit:      { rotulo: "Afastou-se",           sev: "atencao" },
  deviceUnknown:      { rotulo: "Equipamento não cadastrado", sev: "atencao" },
  deviceOffline:      { rotulo: "Ficou offline",        sev: "atencao" },
  deviceInactive:     { rotulo: "Inativo",              sev: "atencao" },
  maintenance:        { rotulo: "Manutenção",           sev: "atencao" },
  driverChanged:      { rotulo: "Troca de motorista",   sev: "atencao" },
  // informativos — histórico, não ação
  deviceOnline:       { rotulo: "Voltou online",        sev: "info" },
  deviceMoving:       { rotulo: "Iniciou movimento",    sev: "info" },
  deviceStopped:      { rotulo: "Parou",                sev: "info" },
  ignitionOn:         { rotulo: "Ignição ligada",       sev: "info" },
  ignitionOff:        { rotulo: "Ignição desligada",    sev: "info" },
  deviceFuelIncrease: { rotulo: "Abastecimento",        sev: "info" },
  commandResult:      { rotulo: "Resposta de comando",  sev: "info" },
  queuedCommandSent:  { rotulo: "Comando enviado",      sev: "info" },
  media:              { rotulo: "Foto ou vídeo recebido", sev: "info" },
  textMessage:        { rotulo: "Mensagem",             sev: "info" },
};

/** Alarmes do campo `attributes.alarm`, que é onde mora o que assusta. */
export const ALARME: Record<string, string> = {
  general: "Alarme geral",
  sos: "SOS / Pânico", vibration: "Vibração", movement: "Movimento",
  lowspeed: "Velocidade baixa", overspeed: "Excesso de velocidade",
  fallDown: "Queda", lowPower: "Energia baixa", lowBattery: "Bateria baixa",
  fault: "Falha", powerOff: "Energia desligada", powerOn: "Energia ligada",
  // ⚠️ `powerOn` e `powerRestored` são alarmes DIFERENTES, e o segundo faltava:
  // o rótulo "Energia restaurada" estava no `powerOn`, então um religamento
  // comum aparecia como se a energia tivesse voltado depois de um corte.
  powerRestored: "Energia restaurada",
  door: "Porta", lock: "Travado", unlock: "Destravado",
  geofence: "Cerca", geofenceEnter: "Entrada em cerca", geofenceExit: "Saída de cerca",
  gpsAntennaCut: "Antena GPS cortada", accident: "Acidente", tow: "Reboque",
  idle: "Ocioso", highRpm: "RPM alto", hardAcceleration: "Aceleração brusca",
  hardBraking: "Frenagem brusca", hardCornering: "Curva brusca",
  laneChange: "Mudança de faixa", fatigueDriving: "Fadiga",
  powerCut: "Corte de energia", jamming: "Jammer detectado",
  temperature: "Temperatura", parking: "Estacionado", bonnet: "Capô",
  footBrake: "Freio de pé", fuelLeak: "Vazamento de combustível",
  tampering: "Violação", removing: "Remoção",
};

export function rotuloEvento(tipo: string, alarme?: string): string {
  if (tipo === "alarm" && alarme) return ALARME[alarme] ?? `Alarme: ${alarme}`;
  return EVENTO[tipo]?.rotulo ?? tipo;
}

export const severidade = (tipo: string): Severidade => EVENTO[tipo]?.sev ?? "info";

export const COR_SEV: Record<Severidade, string> = {
  critico: "var(--color-critical)",
  atencao: "var(--color-warning)",
  info: "var(--color-s1)",
};
